import { Alert, Button, Checkbox, CircularProgress, FormControlLabel } from '@mui/material';
import {
  EmailOutlined,
  GitHub as GitHubIcon,
  HomeOutlined,
  LocationOnOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { FC, FormEvent } from 'react';
import { useState } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { ContactApi, ContactFormData } from '../../services/contactApi';
import { PageBody, PageHeading, PageSurface, Panel, TextInput } from '../../../design-system/components';
import styles from './Contact.module.scss';

const LAB_ADDRESS = 'Via Forlanini, 6, 27100 Pavia PV, Italy';

const directLinks = [
  { label: 'dangelo.unipv.it', description: 'Our lab', icon: <HomeOutlined fontSize="small" />, url: 'https://dangelo.unipv.it/' },
  { label: 'neurocomp.dbbs@unipv.it', description: 'Email', icon: <EmailOutlined fontSize="small" />, url: 'mailto:neurocomp.dbbs@unipv.it' },
  { label: 'github.com/dbbs-lab', description: 'Source code', icon: <GitHubIcon fontSize="small" />, url: 'https://github.com/dbbs-lab' },
];

const emptyForm: ContactFormData = {
  name: '',
  email: '',
  subject: '',
  message: '',
  gdprConsent: false,
};

const Contact: FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ContactFormData>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const handleNavItemClick = (item: string) => {
    switch (item) {
      case 'Model Exploration':
        navigate('/model-exploration');
        break;
      case 'Tools':
        navigate('/tools');
        break;
      case 'Learning':
        navigate('/learning');
        break;
      case 'Contact':
        navigate('/contact');
        break;
      case 'Legal':
        navigate('/legal');
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setSubmitStatus({ type: null, message: '' });
    setFormErrors([]);

    const validation = ContactApi.validateFormData(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await ContactApi.sendContactEmail(formData);

      if (result.success) {
        setSubmitStatus({ type: 'success', message: result.message });
        setFormData(emptyForm);
      } else {
        setSubmitStatus({ type: 'error', message: result.message });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Unexpected error while sending the message. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ContactFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));

    // Validation errors clear as soon as the reader starts correcting them.
    if (formErrors.length > 0) {
      setFormErrors([]);
    }
  };

  const handleGdprConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, gdprConsent: event.target.checked }));

    if (formErrors.length > 0) {
      setFormErrors([]);
    }
  };

  const legalLink = (label: string) => (
    <a
      href="/legal"
      onClick={(e) => {
        e.preventDefault();
        navigate('/legal');
      }}
    >
      {label}
    </a>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header pageTitle="Contact" onNavItemClick={handleNavItemClick} />

      <PageSurface>
        <PageHeading
          title="Get in touch"
          lede="Questions about a model, a dataset or a collaboration. Write to us with the form, or reach the lab directly."
        />

        <PageBody>
          <div className={styles.layout}>
            <Panel title="Message">
              <form onSubmit={handleSubmit} className={styles.form}>
                {submitStatus.type && (
                  <Alert
                    severity={submitStatus.type}
                    onClose={() => setSubmitStatus({ type: null, message: '' })}
                  >
                    {submitStatus.message}
                  </Alert>
                )}

                {formErrors.length > 0 && (
                  <Alert severity="error">
                    <ul className={styles.errors}>
                      {formErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                <div className={styles.form__pair}>
                  <TextInput
                    label="Name"
                    required
                    autoComplete="name"
                    placeholder="Jane Researcher"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    disabled={isSubmitting}
                  />
                  <TextInput
                    label="Email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="name@institution.eu"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    disabled={isSubmitting}
                  />
                </div>

                <TextInput
                  label="Subject"
                  required
                  placeholder="Cerebellar cortex dataset"
                  value={formData.subject}
                  onChange={handleInputChange('subject')}
                  disabled={isSubmitting}
                />

                <TextInput
                  label="Message"
                  multiline
                  rows={6}
                  required
                  hint="Include the model, dataset or tool your question is about."
                  value={formData.message}
                  onChange={handleInputChange('message')}
                  disabled={isSubmitting}
                />

                <FormControlLabel
                  className={styles.consent}
                  control={
                    <Checkbox
                      size="small"
                      checked={formData.gdprConsent}
                      onChange={handleGdprConsentChange}
                      disabled={isSubmitting}
                    />
                  }
                  label={
                    <span className={styles.consent__text}>
                      I consent to the processing of my personal data (name, email, message) for the sole
                      purpose of responding to this request, in accordance with our{' '}
                      {legalLink('Privacy Policy')}. <span className={styles.required}>*</span>
                    </span>
                  }
                />

                <div className={styles.submit}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting || !formData.gdprConsent}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    {isSubmitting ? 'Sending…' : 'Send message'}
                  </Button>
                  <span className={styles.submit__note}>
                    By submitting this form, you agree to our {legalLink('Legal terms & privacy policy')}.
                  </span>
                </div>
              </form>
            </Panel>

            <div className={styles.aside}>
              <Panel title="Location" padded={false} className={styles.location}>
                <iframe
                  className={styles.map}
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2814.8486699839846!2d9.154532915571756!3d45.18394397909868!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4786c6e36e1a0e77%3A0x7e8e8e8e8e8e8e8e!2sVia%20Forlanini%2C%206%2C%2027100%20Pavia%20PV%2C%20Italy!5e0!3m2!1sen!2sus!4v1635789123456!5m2!1sen!2sus"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Our location - ${LAB_ADDRESS}`}
                />
                <div className={styles.address}>
                  <LocationOnOutlined fontSize="small" />
                  <span>{LAB_ADDRESS}</span>
                </div>
              </Panel>

              <Panel title="Direct">
                <ul className={styles.direct}>
                  {directLinks.map((link) => (
                    <li key={link.url}>
                      <span className={styles.direct__icon}>{link.icon}</span>
                      <span className={styles.direct__text}>
                        <span className={styles.direct__description}>{link.description}</span>
                        <a href={link.url} target="_blank" rel="noopener noreferrer">
                          {link.label}
                        </a>
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>
            </div>
          </div>
        </PageBody>
      </PageSurface>

      <Footer />
    </div>
  );
};

export default Contact;
